import { mat4 } from 'gl-matrix';

import { VertexBuffer } from '../../mesh';
import { CubeMapTexture } from '../../texture';
import { RenderSettings } from '../../renderer';
import { Context } from '../../context/Context';
import { Camera } from '../../camera';
import { toRadian } from '../../math';
import { Sky } from './Sky';
import { createSkyCubeGeometry } from './skyCubeGeometry';

/**
 * Image-based skybox: samples a 6-face cubemap texture in the direction of
 * each fragment's view ray. The original sky implementation, kept around for
 * scenes that want a hand-authored / photographed sky.
 */
export class SkyBox extends Sky {
  private buffer: VertexBuffer;
  private vao: WebGLVertexArrayObject;

  public cubeTexture?: CubeMapTexture;

  constructor(
    ctx: Context,
    public rotationSource: RotationSource,
  ) {
    super();
    const geom = createSkyCubeGeometry(ctx);
    this.buffer = geom.buffer;
    this.vao = geom.vao;
  }

  public render(ctx: Context, camera: Camera, settings: RenderSettings) {
    let vertexBuffer = this.buffer;

    if (vertexBuffer.itemCount == 0) {
      return;
    }

    if (!this.cubeTexture) {
      return;
    }

    let gl = ctx.gl;

    let shader = ctx.engine.shaderManager.getSkyBoxShader();
    ctx.setShader(shader);

    gl.bindVertexArray(this.vao);

    let mv = mat4.create();

    mat4.rotateY(mv, mv, toRadian(this.rotationSource.rotation));

    shader.uniformsContainer
      .setProjection(camera.projectionMatrix)
      .setView(mv)
      .setSkyboxSampler(this.cubeTexture)
      .setTintColor(this.tintColor);

    gl.depthMask(false);
    gl.drawArrays(vertexBuffer.glDrawMode, 0, vertexBuffer.itemCount);
    gl.depthMask(true);
  }

}

export type RotationSource = {
  rotation: number;
};
